import React from 'react';
import { useNavigate } from 'react-router-dom';
import { snackbar } from '@occc-app/design-system';

import { CreateWalletPage } from './CreateWalletPage';

import { accumulateErrors, PossibleGraphQLError, routeCreator } from '../utils';
import { WalletFormValues } from '../types';
import { useCreateSurvey, useCreateWallet, useCreateSurveyOffer } from '../hooks';

export const CreateWalletPageContainer: React.FC = () => {
  const navigate = useNavigate();

  const createWallet = useCreateWallet();
  const createSurvey = useCreateSurvey();
  const { createSurveyOffer } = useCreateSurveyOffer();

  const onSubmit = React.useCallback(
    async (values: WalletFormValues) => {
      let errors: PossibleGraphQLError[] = [];
      const handleCreateSurveyOffer = async (surveyUuid: string | undefined) => {
        const hasSurveyIncentive = !!surveyUuid && values.incentivizeResponses.isEnabled;

        if (hasSurveyIncentive) {
          const { errors: createSurveyOfferErrors } = await createSurveyOffer({
            surveyUuid,
            values: values.incentivizeResponses,
          });
          errors = accumulateErrors(errors, createSurveyOfferErrors);
        }
      };

      // Create Wallet step
      const { errors: createWalletErrors, data: createWalletData } = await createWallet(values);
      errors = accumulateErrors(errors, createWalletErrors);

      const walletUuid = createWalletData?.steeringWalletCreate.uuid;
      const walletName = createWalletData?.steeringWalletCreate.name;

      // Create Survey step
      if (walletUuid) {
        const { errors: createSurveyErrors, data: createSurveyData } = await createSurvey(
          walletUuid,
          values,
        );
        const surveyUuid = createSurveyData?.walletSteeringSurveyCreate.uuid;
        errors = accumulateErrors(errors, createSurveyErrors);

        // Create Survey Offer step
        handleCreateSurveyOffer(surveyUuid);
      }

      const isSubmittedWithNoErrors = errors.length === 0;

      if (isSubmittedWithNoErrors) {
        snackbar.success(`${walletName} has been successfully created.`);
        navigate(routeCreator.list());
      } else {
        snackbar.error('Something went wrong. Please try again.');
      }
    },
    [navigate, createWallet, createSurvey, createSurveyOffer],
  );

  return <CreateWalletPage onSubmit={onSubmit} />;
};
